import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LifeBuoy,
  Mail,
  Search,
  ChevronDown,
  Send,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import "react-phone-number-input/style.css";
import { API_BASE_URL } from "@/lib/apiInterceptor";

const APPLICATION_NAME = "InsightSphere";

/* ─────────────────────────── FAQ Data ─────────────────────────── */
const FAQS = [
  {
    q: "How do I get started with InsightSphere?",
    a: "Once your account is provisioned, sign in to the portal and open InsightSphere from the dashboard. Configure your seller profile, upload knowledge bases, and run your first simulation in minutes.",
  },
  {
    q: "What data sources can I connect?",
    a: "InsightSphere supports uploading PDFs, DOCX, and text knowledge bases, plus structured prospect information. Additional connectors (CRM, web crawlers) are available on request.",
  },
  {
    q: "How is my data secured?",
    a: "All data is encrypted in transit (TLS 1.2+) and at rest. Access is governed by role-based permissions and tenant isolation enforced at the API gateway layer.",
  },
  {
    q: "Can I export the generated playbooks?",
    a: "Yes. Playbooks, simulation transcripts, and insights can be exported as PDF or shared via secure link with members of your organization.",
  },
  {
    q: "What is the typical response time for support?",
    a: "Standard support requests are answered within one business day. Priority and enterprise plans include faster SLAs with 24/7 coverage for critical issues.",
  },
];

/* ─────────────────────────── Support Page ─────────────────────────── */
const InsightSphereSupport = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phoneNumber: "",
    subject: "",
    message: "",
    priority: "normal",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const filteredFaqs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQS;
    return FAQS.filter(
      (f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q),
    );
  }, [query]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[e.target.name];
        return next;
      });
    }
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = "Name is required";
    if (!form.email.trim()) {
      next.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = "Enter a valid email";
    }
    if (!form.subject.trim()) next.subject = "Subject is required";
    if (!form.message.trim()) next.message = "Please describe your issue";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setSubmitError(null);
    try {
      console.log("Submitting support request:");
      const response = await fetch(`${API_BASE_URL}/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || undefined,
          phoneNumber: form.phoneNumber || undefined,
          subject: form.subject.trim(),
          message: form.message.trim(),
          priority: form.priority,
          applicationName: APPLICATION_NAME,
        }),
      });
      if (!response.ok) {
        let detail = `Request failed (${response.status})`;
        try {
          const payload = await response.json();
          if (payload?.detail) detail = String(payload.detail);
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      setSubmitted(true);
      setForm({
        name: "",
        email: "",
        company: "",
        phoneNumber: "",
        subject: "",
        message: "",
        priority: "normal",
      });
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const contactChannels = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Support",
      description: "Reach our team for any account or product questions.",
      value: "support-insightsphere@fissionlabs.com",
      href: "mailto:support-insightsphere@fissionlabs.com",
      bgColor: "bg-blue-500",
    },
    // {
    //   icon: <Phone className="w-6 h-6" />,
    //   title: "Phone",
    //   description: "Mon–Fri, 9:00 AM – 6:00 PM (IST)",
    //   value: "+91 80 4000 0000",
    //   href: "tel:+918040000000",
    //   bgColor: "bg-purple-500",
    // },
    // {
    //   icon: <MessageCircle className="w-6 h-6" />,
    //   title: "Live Chat",
    //   description: "Chat with a specialist in real time.",
    //   value: "Start a chat",
    //   href: "#contact-form",
    //   bgColor: "hsl(15 83% 56%)",
    // },
  ];

  return (
    <div className="w-full min-h-screen">
      <div className="w-full px-6 lg:px-12 py-16">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 max-w-4xl mx-auto"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-bg text-white shadow-lg mb-6">
            <LifeBuoy className="w-8 h-8" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-black leading-tight">
            InsightSphere Support
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Get help with InsightSphere — from onboarding and configuration to
            troubleshooting and best practices. Our team is here to keep your
            pipeline moving.
          </p>

          {/* <div className="flex flex-wrap justify-center gap-3">
            {highlights.map((h, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm text-gray-700 shadow-sm"
              >
                <span className="text-orange-500">{h.icon}</span>
                {h.label}
              </div>
            ))}
          </div> */}
        </motion.div>

        {/* Contact Channels */}
        <div className="flex flex-row justify-center gap-6 mb-16 max-w-6xl mx-auto items-center">
          {contactChannels.map((c, index) => (
            <motion.a
              key={index}
              href={c.href}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="card hover:shadow-2xl transition-all duration-300 group block w-[400px] h-[250px]"
            >
              <div
                className={`w-14 h-14 rounded-2xl p-3.5 text-white mb-4 group-hover:scale-110 transition-transform shadow-lg ${
                  c.bgColor.startsWith("hsl") ? "" : c.bgColor
                }`}
                style={
                  c.bgColor.startsWith("hsl")
                    ? { backgroundColor: c.bgColor }
                    : {}
                }
              >
                {c.icon}
              </div>
              <h3 className="text-lg font-bold gradient-text mb-1">
                {c.title}
              </h3>
              <p className="text-gray-600 text-sm mb-3">{c.description}</p>
              <p className="text-gray-900 font-semibold flex items-center gap-2">
                {c.value}
                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </p>
            </motion.a>
          ))}
        </div>

        {/* FAQ + Contact Form */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {/* FAQ */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="card"
          >
            <h2 className="text-2xl font-bold mb-2">
              <span className="text-black">Frequently Asked </span>
              <span style={{ color: "hsl(15 83% 56%)" }}>Questions</span>
            </h2>
            <p className="text-gray-500 text-sm mb-5">
              Search our knowledge base for quick answers.
            </p>

            <div className="relative mb-5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search FAQs..."
                className="input-field pl-10"
              />
            </div>

            <div className="space-y-3">
              {filteredFaqs.length === 0 && (
                <p className="text-sm text-gray-500 py-4 text-center">
                  No results found. Try a different search term.
                </p>
              )}
              {filteredFaqs.map((faq, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={faq.q}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900">
                        {faq.q}
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <p className="px-4 pb-4 text-gray-600 text-sm leading-relaxed">
                            {faq.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            id="contact-form"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="card"
          >
            <h2 className="text-2xl font-bold mb-2">
              <span className="text-black">Submit a </span>
              <span style={{ color: "hsl(15 83% 56%)" }}>Support Request</span>
            </h2>
            <p className="text-gray-500 text-sm mb-5">
              Share as much detail as possible so we can resolve your issue
              quickly.
            </p>

            {submitted ? (
              <motion.div
                className="flex flex-col items-center text-center py-10 gap-4"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="w-16 h-16 rounded-full gradient-bg flex items-center justify-center shadow-lg">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Request Received
                </h3>
                <p className="text-gray-500 max-w-sm">
                  Thanks for reaching out. A support specialist will follow up
                  shortly via the email you provided.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="btn-primary mt-2 px-6 py-2.5"
                >
                  Submit another request
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Jane Smith"
                      className="input-field"
                    />
                    {errors.name && (
                      <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Acme Corp"
                      className="input-field"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                    Work Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="jane@company.com"
                    className="input-field"
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                    Contact Number
                  </label>
                  <div className="flex gap-2 mt-2">
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={form.phoneNumber}
                      onChange={handleChange}
                      placeholder="123-456-7890"
                      className="input-field"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="Brief summary of your issue"
                      className="input-field"
                    />
                    {errors.subject && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.subject}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                      Priority
                    </label>
                    <select
                      name="priority"
                      value={form.priority}
                      onChange={handleChange}
                      className="input-field"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                    Describe Your Issue <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Include steps to reproduce, error messages, and any relevant context..."
                    className="input-field resize-none"
                  />
                  {errors.message && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.message}
                    </p>
                  )}
                </div>

                {submitError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-3 text-base disabled:opacity-70"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        />
                      </svg>
                      Sending…
                    </span>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Request
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>

      <footer className="w-full py-6 px-6 border-t border-gray-100 text-center text-sm text-gray-500 mt-8">
        © {new Date().getFullYear()} InsightSphere Intelligence Platform. All
        rights reserved.
      </footer>
    </div>
  );
};

export default InsightSphereSupport;
